import { useMutation } from "@apollo/client";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { PersonPicker, PersonPickerValue } from "components/PersonPicker";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { getFullName } from "encamp-shared/src/utils/name";
import { getWorkOrFirstPhoneNumber } from "encamp-shared/src/utils/phoneNumber";
import { sortBy } from "lodash-es";
import { useState } from "react";

import {
  type DefaultVerifierRow,
  TENANT_DEFAULT_VERIFIERS,
  UPSERT_TENANT_DEFAULT_VERIFIER,
} from "./queries";
import { stateLabel } from "./stateLabel";

// MUI selects need a non-empty value for the "All other states" option.
const ALL_STATES = "ALL";

const STATE_OPTIONS = sortBy(Object.entries(STATE_NAMES), 1);

export function DefaultVerifierDialog({
  tenantId,
  existing,
  onClose,
}: {
  tenantId: string;
  existing: DefaultVerifierRow | null;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const [state, setState] = useState(existing?.state ?? ALL_STATES);
  const [titleOverride, setTitleOverride] = useState(existing?.titleOverride ?? "");
  const [person, setPerson] = useState<PersonPickerValue | null>(existing?.person ?? null);
  const [upsert, { loading }] = useMutation(UPSERT_TENANT_DEFAULT_VERIFIER, {
    refetchQueries: [TENANT_DEFAULT_VERIFIERS],
  });

  const onSave = async () => {
    if (!person) return;
    try {
      await upsert({
        variables: {
          input: {
            id: existing?.id,
            tenantId,
            state: state === ALL_STATES ? null : state,
            personId: person.id,
            titleOverride: titleOverride.trim() || null,
          },
        },
      });
      alerts.success("Default verifier saved");
      onClose();
    } catch (err) {
      alerts.error("Error saving default verifier", err);
    }
  };

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      title={existing ? "Edit default verifier" : "Add default verifier"}
      onClose={onClose}
      primaryActionButtonProps={{ loading, disabled: !person, onClick: onSave }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label="State"
            value={state}
            onChange={(event) => setState(event.target.value)}
            helperText="Reports for facilities in this state use this verifier"
            sx={{ minWidth: 240 }}
          >
            <MenuItem value={ALL_STATES}>{stateLabel(null)}</MenuItem>
            {STATE_OPTIONS.map(([abbreviation, name]) => (
              <MenuItem key={abbreviation} value={abbreviation}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Title override"
            value={titleOverride}
            onChange={(event) => setTitleOverride(event.target.value)}
            helperText="Shown on reports instead of the contact's title, e.g. to fit a state's length limit"
            sx={{ flex: 1 }}
          />
        </Stack>
        <PersonPicker
          label="Verifier"
          tenantId={tenantId}
          allowCreate={false}
          showDetails
          value={person}
          onSelectPerson={setPerson}
        />
        {person && (
          <Box>
            <Typography variant="subtitle2">Pre-filled on reports as</Typography>
            <Typography variant="body2">{getFullName(person)}</Typography>
            <Typography variant="body2">{titleOverride.trim() || person.title}</Typography>
            <Typography variant="body2">{person.email}</Typography>
            <Typography variant="body2">{getWorkOrFirstPhoneNumber(person.phones)}</Typography>
          </Box>
        )}
      </Stack>
    </Dialog>
  );
}
